import { Environment } from 'constants/environment';
import { MerchantAliases } from 'constants/merchantAlias';
import { MerchantPlatformNames } from 'constants/merchantPlatforms';
import { Role } from 'constants/roles';

import { useEnvironment } from 'hooks/useEnvironment';
import { usePermissions } from 'hooks/usePermissions';
import { Merchant } from 'types/embedded/merchant';

export const enum MerchantTab {
  ProviderSettings = 'providerSettings',
  Accesses = 'accesses',
}

type TabVisibilityContext = {
  record?: Merchant;
  environment: Environment;
  roles: Role[];
};

type TabVisibilityChecker = (context: TabVisibilityContext) => boolean;

const providerSettingsChecker: TabVisibilityChecker = ({ record, environment }) => {
  if (!record) {
    return false;
  }

  if (environment === Environment.Development) {
    return record?.platform?.name === MerchantPlatformNames.Obelis;
  }

  if (environment === Environment.Sandbox) {
    return [
      MerchantAliases.TestMerchantPaylands,
      MerchantAliases.ObelisBrazil,
      MerchantAliases.ObelisBrazilStage,
    ].includes(record.alias);
  }

  if (environment === Environment.Production) {
    return [MerchantAliases.ObelisBrazil].includes(record.alias);
  }

  return true;
};

const accessesChecker: TabVisibilityChecker = ({ roles }) => {
  return !roles?.includes(Role.Payment);
};

const TAB_VISIBILITY_CHECKERS: Partial<Record<MerchantTab, TabVisibilityChecker>> = {
  [MerchantTab.ProviderSettings]: providerSettingsChecker,
  [MerchantTab.Accesses]: accessesChecker,
};

export const useTabVisibility = (record?: Merchant) => {
  const { environment } = useEnvironment();
  const { roles } = usePermissions();

  const checkTabVisible = (tabName: MerchantTab): boolean => {
    const checker = TAB_VISIBILITY_CHECKERS[tabName];

    if (!checker) {
      return true;
    }

    return checker({ record, environment: environment as Environment, roles });
  };

  return { checkTabVisible };
};
